import { NextPage } from 'next'
import { useTranslation } from '../../utils/convergence'
import React, { ReactElement, useState } from 'react'
import { Head } from '../../../src/component'
import { useAppDispatch, useConsumerSelector, sendResetLink, useMobileLogin, useCountry } from '../../../src/state'
import { unwrapResult } from '@reduxjs/toolkit'
import { PasswordResetRequired } from '../../../src/component/PasswordResetRequired'
import { EmailSent } from '../../../src/component/EmailSent'
import { useToast, useIsDesignLanguage, DesignLanguage } from '@afterpaytouch/core'
import { capture } from '@afterpaytouch/integrations/monitoring'
import { Country } from '@afterpay/types'

export const PasswordResetRequiredPage: NextPage = (): ReactElement => {
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { email = '', mobile = '' } = useConsumerSelector()
  const { isMobileLogin } = useMobileLogin()
  const country = useCountry()
  const [sendButtonLoading, setSendButtonLoading] = useState(false)
  const [emailSent, setEmailSent] = useState(false)
  const toast = useToast()
  const pageTitle = `${t('common:brandName')}`
  const handleSendButtonClick = async (): Promise<void> => {
    try {
      setSendButtonLoading(true)
      unwrapResult(await dispatch(sendResetLink(isMobileLogin ? { consumerPhoneNumber: mobile, phoneNumberCountryCode: country } : { consumerEmail: email })))
      // Only show success toast on resend
      if (emailSent) {
        toast({ message: t('passwordResetRequired:linkSent'), kind: 'Success' })
      }
      setEmailSent(true)
    } catch (e) {
      toast({ message: `${t('common:error:unknown')}`, kind: 'Failure' })
      capture.error('Error during sending password reset link', { ...e })
    } finally {
      setSendButtonLoading(false)
    }
  }

  const showResetRequiredContent = (isArcadeDesignLanguage && country === Country.US) || !emailSent

  return (
    <>
      <Head title={pageTitle} />
      {showResetRequiredContent && (
        <PasswordResetRequired
          email={email}
          isMobileLogin={isMobileLogin}
          isLoading={sendButtonLoading}
          clickHandler={handleSendButtonClick}
          emailSent={emailSent}
        />
      )}
      {emailSent && <EmailSent email={email} isMobileLogin={isMobileLogin} clickHandler={handleSendButtonClick} />}
    </>
  )
}
